import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Add, Analytics, Create } from "@mui/icons-material";
import { myClassesServices } from "../../services";
import ClassCreator from "../myExparience/classes/ClassCreator";
import { initialClassState } from "../myExparience/classes/classInitialisation";
import ClassesManagementStats from "./ClassesManagementStats";

const PREFIX = "ClassesManagement";

const classes = {
  classesListMainContainer: `${PREFIX}-classesListMainContainer`,
  classesListElementContainer: `${PREFIX}-classesListElementContainer`,
  classesListElementTitle: `${PREFIX}-classesListElementTitle`,
  generatingPreviewContainer: `${PREFIX}-generatingPreviewContainer`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.classesListMainContainer}`]: {
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    maxWidth: "650px",
    width: "100%",
    padding: "0.5rem",
    marginTop: "1rem",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#d3af5f",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
    [theme.breakpoints.between("xs", "sm")]: {
      marginBottom: "5rem",
    },
  },
  [`& .${classes.classesListElementContainer}`]: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem",
    marginBottom: "0.5rem",
    minHeight: "80px",
    width: "100%",
  },
  [`& .${classes.classesListElementTitle}`]: {
    marginLeft: "1rem",
    maxWidth: "160px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  [`& .${classes.generatingPreviewContainer}`]: {
    padding: "2rem 1rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: "350px",
    textAlign: "center",
    [theme.breakpoints.between("xs", "sm")]: {
      padding: "2rem 3rem",
    },
  },
}));

const ClassesManagement = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [groups, setGroups] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState({
    open: false,
    group: null,
  });
  const [isStats, setIsStats] = useState(false);
  const [loading, setLoading] = useState(true);

  const isAdmin = useSelector((state) => state.user.currentUser.account.role);
  const myAccountId = useSelector((state) => state.user.currentUser.account.id);

  useEffect(() => {
    myClassesServices
      .getGroups(isAdmin, [])
      .then((data) => {
        setGroups(data);
        setLoading(false);
      })
      .catch((error) => {
        enqueueSnackbar(t("toasts.fetchGroupsError"), { variant: "error" });
        // setLoading(false);
      });
  }, [enqueueSnackbar, t, isAdmin]);

  const addGroupToExparienceAfterCreation = (createdClass) => {
    const newGroups = [...groups];
    const index = newGroups.findIndex((group) => group.id === createdClass.id);
    if (index < 0) newGroups.push(createdClass);
    else newGroups[index] = createdClass;
    setGroups(newGroups);
  };

  const openStats = (id) => {
    setIsStats(true);
  };

  if (loading)
    return (
      <div className="loading-container">
        <CircularProgress size={50} />
        <Typography>{t("loaders.fetching")}</Typography>
      </div>
    );
  else {
    return (
      <Root sx={{ padding: { xs: "1rem 0", sm: "1rem" } }}>
        <ClassCreator
          open={selectedGroup.open}
          selectedClass={selectedGroup.group}
          isClassManager={true}
          onClose={() => setSelectedGroup({ open: false, group: null })}
          isAdmin={isAdmin}
          addGroupToExparienceAfterCreation={addGroupToExparienceAfterCreation}
        />
        {!isStats ? (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="h4">
                {t("classManagement.classTitle")}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "1.5rem",
              }}
            >
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() =>
                  setSelectedGroup({ open: true, group: initialClassState })
                }
              >
                {t("classManagement.addClass")}
              </Button>
              <Card elevation={12} className={classes.classesListMainContainer}>
                {groups?.map((item, index) => {
                  return (
                    <Card
                      key={`exparience-group-${item.id}-${index + 1}`}
                      elevation={8}
                      className={classes.classesListElementContainer}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.imageUrl}
                            style={{
                              width: "50px",
                              height: "50px",
                              borderRadius: "10px",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: "50px",
                              height: "50px",
                              borderRadius: "10px",
                            }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          className={classes.classesListElementTitle}
                        >
                          {item.name}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton
                          onClick={() => {
                            setSelectedGroup({ open: true, group: item });
                          }}
                          color="primary"
                        >
                          <Create />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setSelectedGroup({ open: false, group: item });
                            openStats(item.createdBy.account_id);
                          }}
                          color="primary"
                        >
                          <Analytics />
                        </IconButton>
                      </Box>
                    </Card>
                  );
                })}
              </Card>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <ClassesManagementStats
              setIsStats={setIsStats}
              accountId={myAccountId}
              group={selectedGroup.group}
            />
          </Box>
        )}
      </Root>
    );
  }
};

export default ClassesManagement;
